
import { Globe, Info } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { type NetworkRule } from "@/hooks/useNetworkRules";
import { type GroupedRule } from "./NetworkRulesUtils";

interface SystemWideRulesTableProps {
  filteredGroups: GroupedRule[];
  onRuleClick: (rule: NetworkRule) => void;
  
}

export const SystemWideRulesTable = ({ filteredGroups, onRuleClick }: SystemWideRulesTableProps) => {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <ScrollArea className="w-full">
        <div className="w-full overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[250px]">Rule Name</TableHead>
                <TableHead className="w-[120px]">URLs</TableHead>
                <TableHead className="w-[120px]">Scope</TableHead>
                <TableHead className="w-[120px]">Modified</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredGroups.flatMap(group => 
                group.rules.map(rule => (
                  <TableRow 
                    key={`rule-${rule.id}`}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => onRuleClick(rule)}
                  >
                    <TableCell>
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center mr-3">
                          <Globe className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="font-medium text-gray-900">{rule.name}</div>
                          {rule.description && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-xs">{rule.description}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                         </div>
                       </div>
                     </TableCell>
                     <TableCell>
                       <div>
                         <div className="text-sm text-gray-600">
                           {rule.urls.length} URL{rule.urls.length !== 1 ? 's' : ''}
                         </div>
                         <div className="text-xs text-gray-500">
                           Socket
                         </div>
                       </div>
                     </TableCell>
                     <TableCell>
                       <div className="text-sm text-gray-600">
                         {rule.scope.devices} devices
                       </div>
                       <div className="text-xs text-gray-500">
                         {rule.scope.groups.length} group{rule.scope.groups.length !== 1 ? 's' : ''}
                       </div>
                     </TableCell>
                     <TableCell className="text-sm text-gray-600">
                       {rule.lastModified}
                     </TableCell>
                   </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </ScrollArea>
    </div>
  );
};
